/**
 * La oferta de instalacion del navegador, para React.
 *
 * La atrapa el guion del `<head>` (`install-capture.ts`) y la deja en la
 * ventana; esto la lee y avisa cuando cambia. Solo Chrome, Edge y familia la
 * dan; en los demas `useInstallOffer` es siempre falso y la instalacion va por
 * pasos (`install-path.ts`).
 *
 * Tambien vive aqui el "abrelo a mano": Configuracion abre el mismo instalador
 * que se ofrece solo, sin contar como oferta ni esperar turno —lo pidio la
 * persona—.
 */

import { useSyncExternalStore } from 'react';
import { INSTALL_EVENT } from './install-capture.js';

interface BrowserInstallOffer extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type InstallWindow = Window & {
  __abarrotesInstallOffer?: BrowserInstallOffer | null;
  __abarrotesInstalled?: boolean;
};

const win = (): InstallWindow => window as InstallWindow;

const subscribeOffer = (notify: () => void): (() => void) => {
  window.addEventListener(INSTALL_EVENT, notify);
  return () => window.removeEventListener(INSTALL_EVENT, notify);
};

/** El navegador ofrecio instalarla y la oferta sigue sin usarse. */
export const useInstallOffer = (): boolean =>
  useSyncExternalStore(
    subscribeOffer,
    () => Boolean(win().__abarrotesInstallOffer),
    () => false,
  );

/** Se acaba de instalar desde esta misma pestaña (`appinstalled`). */
export const useJustInstalled = (): boolean =>
  useSyncExternalStore(
    subscribeOffer,
    () => win().__abarrotesInstalled === true,
    () => false,
  );

export type InstallOutcome = 'accepted' | 'dismissed' | 'no-offer';

/**
 * Abre el cuadro de instalacion del navegador. Tiene que llamarse DENTRO del
 * toque de la persona: fuera de uno, el navegador lo ignora sin decir nada.
 */
export async function launchInstall(): Promise<InstallOutcome> {
  const w = win();
  const offer = w.__abarrotesInstallOffer;
  if (!offer) return 'no-offer';
  // Una oferta sirve UNA sola vez. Si se cancela, el navegador da otra en una
  // carga futura y el guion la vuelve a atrapar.
  w.__abarrotesInstallOffer = null;
  try {
    await offer.prompt();
    const { outcome } = await offer.userChoice;
    return outcome;
  } finally {
    w.dispatchEvent(new Event(INSTALL_EVENT));
  }
}

/**
 * Si el navegador sabe que ya esta instalada en este aparato aunque se este
 * viendo en una pestaña. Chrome lo dice (`getInstalledRelatedApps`, con
 * `related_applications` en el manifiesto); los demas no, y se toma como no.
 */
export async function isAlreadyOnDevice(): Promise<boolean> {
  const nav = navigator as Navigator & {
    getInstalledRelatedApps?: () => Promise<Array<{ platform: string }>>;
  };
  if (!nav.getInstalledRelatedApps) return false;
  try {
    const apps = await nav.getInstalledRelatedApps();
    return apps.some((a) => a.platform === 'webapp');
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Abrirlo a mano
// ---------------------------------------------------------------------------

let requested = false;
const requestListeners = new Set<() => void>();

const notifyRequest = (): void => {
  for (const listener of requestListeners) listener();
};

export function openInstaller(): void {
  requested = true;
  notifyRequest();
}

export function closeInstaller(): void {
  requested = false;
  notifyRequest();
}

const subscribeRequest = (listener: () => void): (() => void) => {
  requestListeners.add(listener);
  return () => {
    requestListeners.delete(listener);
  };
};

export const useInstallerRequested = (): boolean =>
  useSyncExternalStore(
    subscribeRequest,
    () => requested,
    () => false,
  );

// ---------------------------------------------------------------------------
// Turno con el recorrido de permisos
// ---------------------------------------------------------------------------

/*
 * Lo que el instalador le dice al recorrido de permisos (`PermissionsTour`):
 *
 *   - `busy`: todavia decide si ofrecerse, va a ofrecerse o esta en pantalla. El
 *     recorrido no arranca hasta que se desocupa: instalar va primero, porque en
 *     iPhone los avisos solo llegan con la app instalada y pedirlos antes es
 *     pedirlos en la caja equivocada.
 *   - `open`: esta en pantalla. Si el recorrido ya iba —lo abrio el mismo, para
 *     instalar—, se aparta mientras tanto y vuelve al cerrarse.
 *
 * Arranca ocupado: hasta que el instalador decide, no se sabe si se va a
 * ofrecer, y adivinar que no es encimar las dos hojas.
 */
let installerBusy = true;
let installerOpen = false;
const presenceListeners = new Set<() => void>();

export function setInstallerPresence(busy: boolean, open: boolean): void {
  if (busy === installerBusy && open === installerOpen) return;
  installerBusy = busy;
  installerOpen = open;
  for (const listener of presenceListeners) listener();
}

const subscribePresence = (listener: () => void): (() => void) => {
  presenceListeners.add(listener);
  return () => {
    presenceListeners.delete(listener);
  };
};

export const useInstallerBusy = (): boolean =>
  useSyncExternalStore(subscribePresence, () => installerBusy, () => true);

export const useInstallerOpen = (): boolean =>
  useSyncExternalStore(subscribePresence, () => installerOpen, () => false);
